import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { useApiClient } from '@/shared/hooks/useApiClient';
import { createClientsApi } from '@/features/admin/api/clientsApi';
import { useTenant } from '@/shared/hooks/useTenant';

export interface ClientSearchResult {
  id: number;
  customer_number: string;
  name: string;
  email: string;
  phone_mobile: string;
  phone_landline: string;
}

export const useClientSearch = (searchTerm: string, enabled: boolean) => {
  const { tenant } = useTenant();
  const apiClient = useApiClient();
  const clientsApi = createClientsApi(apiClient);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  const { data, isLoading, error } = useQuery({
    queryKey: ['clientSearch', tenant?.tenantId, debouncedSearchTerm],
    queryFn: async () => {
      const term = debouncedSearchTerm.trim();
      if (!term || term.length < 2) {
        return { data: [], total: 0 };
      }

      const searchTermLower = term.toLowerCase();

      // Build query as a flat array (implicit AND), matching the pattern in useClients.ts.
      // Each element in the array is a condition; compound OR/AND are single elements.
      const queryParts: unknown[][] = [["equal", "enabled", "ENABLED"]];

      if (searchTermLower.includes('(')) {
        // Format: "name (id)" — search by both name and customer_number
        const parts = searchTermLower.split('(');
        const namePart = parts[0].trim();
        const idPart = parts[1]?.replace(/[^\d]/g, '').trim();
        if (namePart) queryParts.push(["like", "name", `%${namePart}%`]);
        if (idPart) queryParts.push(["like", "customer_number", `%${idPart}%`]);
      }
      else if (/^\d+$/.test(searchTermLower)) {
        // ECOPD #70: pure digits should match customer_number OR a phone
        // number, since users typing "5551234" are usually looking up a
        // client by phone (mobile or landline). Previously only the
        // customer_number branch fired and phone lookups silently returned
        // zero results.
        queryParts.push([
          "or",
          ["like", "customer_number", `%${searchTermLower}%`],
          ["like", "phone_mobile", `%${searchTermLower}%`],
          ["like", "phone_landline", `%${searchTermLower}%`],
        ]);
      }
      else {
        const terms = searchTermLower.split(/\s+/).filter(t => t);
        if (terms.length > 1) {
          // Multi-word: each word must appear in name (each pushed as separate AND condition)
          terms.forEach(t => queryParts.push(["like", "name", `%${t}%`]));
        } else {
          // Single word: search name OR customer_number OR email OR phone
          // (phone match covers formatted strings like "555-1234").
          queryParts.push([
            "or",
            ["like", "name", `%${searchTermLower}%`],
            ["like", "customer_number", `%${searchTermLower}%`],
            ["like", "email", `%${searchTermLower}%`],
            ["like", "phone_mobile", `%${searchTermLower}%`],
            ["like", "phone_landline", `%${searchTermLower}%`],
          ]);
        }
      }

      const query = queryParts;

      // Build query as a flat array (implicit AND), matching the pattern used in useClients.ts.
      // getClientDetails select order (must match clientsApi.ts getClientDetails select array):
      // 0:id, 1:customer_number, 2:name, 3:enabled, 4:medicaid_number, 5:medicare_number,
      // 6:ssn_encrypted, 7:date_of_birth, 8:gender, 9:language_code, 10:email,
      // 11:phone_mobile, 12:phone_landline, 13:vehicle_req, 14:pickup_delay, 15:dropoff_delay,
      // 16:last_ride_timestamp, 17:creation_ts, 18:home_street_number, 19:home_street,
      // 20:home_street_suffix, 21:home_city, 22:home_postcode, 23:default_device.id,
      // 24:default_device.name, 25:default_device.code, 26:default_provider.id,
      // 27:home_location.id, 28:selfservice_profile.id, 29:selfservice_profile.status
      const response = await clientsApi.getClientDetails(
        0,
        10,
        query,
        [
          ["name", "asc_nulls_last"],
          ["customer_number", "asc_nulls_last"],
          ["id", "asc"],
        ]
      );

      const results: ClientSearchResult[] = (response.data || []).map((row: (string | number | null)[]) => {
        return {
          id: row[0] as number,
          customer_number: row[1] as string,
          name: row[2] as string,
          email: (row[10] as string) || '',
          phone_mobile: (row[11] as string) || '',
          phone_landline: (row[12] as string) || '',
        };
      });

      return {
        data: results,
        total: response.total || 0,
      };
    },
    enabled: !!tenant && enabled && debouncedSearchTerm.length >= 2,
  });

  return {
    clients: data?.data || [],
    total: data?.total || 0,
    isLoading,
    error,
  };
};